import React, { useEffect, useState, useMemo } from "react";
import { Portal, Packages } from "@/lib/api";
import { Trash2, Plus, ShoppingCart, Send, Search } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLES = {
  new: "bg-slate-800 text-slate-300 border border-white/10",
  reviewing: "bg-amber-500/15 text-amber-300 border border-amber-500/30",
  fulfilled: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
  cancelled: "bg-rose-500/15 text-rose-300 border border-rose-500/30",
};

const emptyLine = () => ({
  product_id: "", sku: "", name: "", quantity: 1, unit_price: 0, unit: "each", package_slug: "",
  isCustom: false,
});

const PortalReorder = () => {
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [items, setItems] = useState([emptyLine()]);
  const [notes, setNotes] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [myOrders, setMyOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [pickerOpenAt, setPickerOpenAt] = useState(null); // index of the line that opened the picker
  const [pickerQ, setPickerQ] = useState("");

  const refresh = async () => {
    try { setMyOrders(await Portal.orders.mine()); }
    catch { /* ignore */ }
    finally { setLoadingOrders(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const [ps, pk] = await Promise.all([Portal.products.list(), Packages.list(true)]);
        setProducts(ps || []);
        setPackages(pk || []);
      } catch { /* ignore */ }
    })();
    refresh();
  }, []);

  const setItem = (idx, patch) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const addItem = () => setItems((prev) => [...prev, emptyLine()]);
  const addCustom = () => setItems((prev) => [...prev, { ...emptyLine(), isCustom: true }]);

  const pickProduct = (idx, p) => {
    setItem(idx, {
      product_id: p.id, sku: p.sku || "", name: p.name,
      unit_price: Number(p.price || 0), unit: p.unit || "each",
      isCustom: false,
    });
    setPickerOpenAt(null);
    setPickerQ("");
  };

  const filteredProducts = useMemo(() => {
    const needle = pickerQ.toLowerCase().trim();
    return products.filter((p) => {
      if (!p.in_stock) return false;
      if (!needle) return true;
      return (p.name + " " + p.sku + " " + p.category).toLowerCase().includes(needle);
    });
  }, [products, pickerQ]);

  const total = useMemo(
    () => items.reduce((s, it) => s + (Number(it.unit_price) || 0) * (Number(it.quantity) || 1), 0),
    [items]
  );

  const submit = async () => {
    const cleanItems = items
      .filter((i) => i.name.trim())
      .map((i) => ({
        product_id: i.product_id || "",
        package_slug: i.package_slug || "",
        sku: i.sku || "",
        name: i.name.trim(),
        quantity: Math.max(1, Number(i.quantity) || 1),
        unit_price: Number(i.unit_price) || 0,
        unit: i.unit || "each",
      }));
    if (cleanItems.length === 0) { toast.error("Add at least one item"); return; }
    setSubmitting(true);
    try {
      await Portal.orders.create({ items: cleanItems, notes, contact_phone: phone, delivery_address: address });
      toast.success("Order request sent — ATC will follow up shortly");
      setItems([emptyLine()]);
      setNotes(""); setPhone(""); setAddress("");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to submit"); }
    finally { setSubmitting(false); }
  };

  return (
    <section className="p-10" data-testid="portal-reorder-tab">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.22em] text-atc-blue mb-2">Portal</div>
        <h1 className="font-display font-black text-4xl text-white">Order Equipment</h1>
        <p className="text-slate-400 text-sm mt-2">Pick from the catalog for pricing, or type a custom item. The estimate below is not a final quote.</p>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,360px)] gap-6">
        <div className="glass rounded-2xl p-6 space-y-4" data-testid="portal-reorder-form">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-semibold text-white flex items-center gap-2">
              <ShoppingCart className="h-4 w-4 text-atc-blue" /> Items
            </div>
            <div className="flex items-center gap-3">
              <button onClick={addItem} className="text-xs text-atc-blue hover:text-white flex items-center gap-1" data-testid="portal-reorder-add-item">
                <Plus className="h-3.5 w-3.5" /> Add from catalog
              </button>
              <button onClick={addCustom} className="text-xs text-slate-400 hover:text-white flex items-center gap-1" data-testid="portal-reorder-add-custom">
                <Plus className="h-3.5 w-3.5" /> Custom item
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {items.map((it, idx) => {
              const lineTotal = (Number(it.unit_price) || 0) * (Number(it.quantity) || 1);
              return (
                <div key={idx} className="rounded-lg border border-white/5 bg-slate-950/40 p-3 space-y-2" data-testid={`portal-line-${idx}`}>
                  <div className="grid grid-cols-[minmax(0,1fr)_80px_auto] gap-2 items-start">
                    <div className="relative">
                      {it.isCustom ? (
                        <input
                          value={it.name}
                          onChange={(e) => setItem(idx, { name: e.target.value })}
                          placeholder="Custom item name"
                          className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-white/10 text-sm text-white placeholder:text-slate-500"
                          data-testid={`portal-item-name-${idx}`}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setPickerOpenAt(idx); setPickerQ(""); }}
                          className="w-full text-left px-3 py-2 rounded-lg bg-slate-950 border border-white/10 text-sm text-white hover:border-atc-blue/40 transition flex items-center justify-between gap-2"
                          data-testid={`portal-item-pick-${idx}`}
                        >
                          <span className={it.name ? "text-white" : "text-slate-500"}>
                            {it.name || "Pick a product from catalog…"}
                          </span>
                          <Search className="h-3.5 w-3.5 text-slate-500 shrink-0" />
                        </button>
                      )}
                      {it.sku && <div className="mt-1 text-[10px] text-slate-500">SKU: {it.sku}</div>}
                    </div>
                    <input
                      type="number"
                      min={1}
                      value={it.quantity}
                      onChange={(e) => setItem(idx, { quantity: e.target.value })}
                      className="px-3 py-2 rounded-lg bg-slate-950 border border-white/10 text-sm text-white text-center"
                      data-testid={`portal-item-qty-${idx}`}
                    />
                    <button
                      onClick={() => removeItem(idx)}
                      disabled={items.length === 1}
                      className="p-2 text-slate-500 hover:text-rose-400 disabled:opacity-30"
                      data-testid={`portal-item-remove-${idx}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  {(it.unit_price > 0 || it.isCustom) && (
                    <div className="flex items-center justify-between text-xs text-slate-400 pl-1">
                      <span>
                        {it.unit_price > 0
                          ? <>${Number(it.unit_price).toFixed(2)} / {it.unit}</>
                          : <span className="text-slate-500 italic">Custom — no price</span>}
                      </span>
                      {lineTotal > 0 && (
                        <span className="text-atc-green tabular-nums" data-testid={`portal-item-line-total-${idx}`}>
                          ${lineTotal.toFixed(2)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {total > 0 && (
            <div className="flex items-center justify-between border-t border-white/10 pt-3 mt-2" data-testid="portal-reorder-total">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Estimated total</div>
              <div className="text-2xl font-black text-atc-green tabular-nums">${total.toFixed(2)}</div>
            </div>
          )}
          {total > 0 && (
            <div className="text-[10px] text-slate-500 -mt-2">Pricing shown is an estimate. ATC will confirm final quote with taxes, freight, and any discounts.</div>
          )}

          <div className="pt-2 grid sm:grid-cols-2 gap-3">
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Best contact phone" className="px-3 py-2 rounded-lg bg-slate-950 border border-white/10 text-sm text-white placeholder:text-slate-500" data-testid="portal-reorder-phone" />
            <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Delivery address (optional)" className="px-3 py-2 rounded-lg bg-slate-950 border border-white/10 text-sm text-white placeholder:text-slate-500" data-testid="portal-reorder-address" />
          </div>

          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for ATC (timing, special instructions, etc.)" className="w-full px-3 py-2 rounded-lg bg-slate-950 border border-white/10 text-sm text-white placeholder:text-slate-500" data-testid="portal-reorder-notes" />

          <div className="flex justify-end">
            <button
              onClick={submit}
              disabled={submitting}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full brand-gradient text-white text-sm font-semibold hover:opacity-95 disabled:opacity-60"
              data-testid="portal-reorder-submit"
            >
              <Send className="h-4 w-4" /> {submitting ? "Sending…" : "Submit request"}
            </button>
          </div>
        </div>

        <div className="glass rounded-2xl p-6" data-testid="portal-my-orders">
          <div className="text-sm font-semibold text-white mb-3">Your recent requests</div>
          {loadingOrders ? (
            <div className="text-xs text-slate-400">Loading…</div>
          ) : myOrders.length === 0 ? (
            <div className="text-xs text-slate-500">No orders yet — submit one on the left.</div>
          ) : (
            <div className="space-y-3">
              {myOrders.map((o) => {
                const est = o.items.reduce((s, i) => s + (Number(i.unit_price) || 0) * (Number(i.quantity) || 1), 0);
                return (
                  <div key={o.id} className="border border-white/5 rounded-lg p-3 text-sm">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${STATUS_STYLES[o.status] || STATUS_STYLES.new}`}>{o.status}</span>
                      <span className="text-[10px] text-slate-500">{new Date(o.created_at).toLocaleDateString()}</span>
                    </div>
                    <ul className="text-xs text-slate-300 space-y-0.5">
                      {o.items.map((i, ix) => <li key={ix}>{i.quantity}× {i.name}</li>)}
                    </ul>
                    {est > 0 && (
                      <div className="mt-2 pt-2 border-t border-white/5 flex justify-between text-[10px] uppercase tracking-wider">
                        <span className="text-slate-500">Est.</span>
                        <span className="text-atc-green tabular-nums">${est.toFixed(2)}</span>
                      </div>
                    )}
                    {o.notes && <div className="text-[11px] text-slate-500 mt-2 italic">{o.notes}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Product picker overlay */}
      {pickerOpenAt !== null && (
        <div
          className="fixed inset-0 z-[9990] flex items-start justify-center pt-24 px-4 bg-slate-950/70 backdrop-blur-sm"
          onClick={() => setPickerOpenAt(null)}
          data-testid="portal-product-picker"
        >
          <div className="w-full max-w-xl glass rounded-2xl p-4 max-h-[70vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <input
                autoFocus
                value={pickerQ}
                onChange={(e) => setPickerQ(e.target.value)}
                placeholder="Search catalog…"
                className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-slate-950 border border-white/10 text-sm text-white placeholder:text-slate-500"
                data-testid="portal-picker-search"
              />
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-white/5">
              {products.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400">
                  No catalog yet — ATC hasn&apos;t published products. Use <strong>Custom item</strong> to request anything you need.
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-400">No matches. Try a different keyword or add a custom item.</div>
              ) : (
                filteredProducts.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => pickProduct(pickerOpenAt, p)}
                    className="w-full flex items-center gap-3 p-3 text-left hover:bg-white/[0.03] rounded-lg"
                    data-testid={`portal-picker-item-${p.id}`}
                  >
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="h-12 w-12 rounded-lg object-cover bg-slate-950" />
                    ) : (
                      <div className="h-12 w-12 rounded-lg bg-atc-blue/15 border border-atc-blue/30 flex items-center justify-center">
                        <ShoppingCart className="h-5 w-5 text-atc-blue" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-semibold truncate">{p.name}</div>
                      <div className="text-[11px] text-slate-400 truncate">
                        {p.category}{p.sku ? ` · SKU ${p.sku}` : ""} · per {p.unit}
                      </div>
                    </div>
                    <div className="text-atc-green text-sm font-semibold tabular-nums shrink-0">${Number(p.price || 0).toFixed(2)}</div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PortalReorder;
